import { useState } from "react";

import { api, ApiError, type SafeUser } from "../lib/api";
import { EulaModal } from "../components/eula/EulaModal";

interface LoginPageProps {
  onLogin: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Just the "read the EULA" link below - EULA *acceptance* is gated after
  // login (see App.tsx), not here: correct credentials always sign the user
  // in, and the server enforces acceptance on every request past that point.
  const [isEulaOpen, setIsEulaOpen] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post<SafeUser>("/api/v1/login", { username, password });
      onLogin();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
          const lockedUntil = (err.body as { lockedUntil?: string } | null)
            ?.lockedUntil;
          if (lockedUntil) {
            const msLeft = new Date(lockedUntil).getTime() - Date.now();
           
            const minutesLeft = Math.max(1, Math.ceil(msLeft / 60000));
            setError(
              `Account is temporarily locked. Try again in ${minutesLeft} minute${minutesLeft === 1 ? "" : "s"}.`,
            );
          } else {
          setError("Account is temporarily locked. Try again in a few minutes.");
          }
      } else {
        setError(err instanceof Error ? err.message : "Login failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen w-screen grid-cols-1 lg:grid-cols-[1fr_minmax(380px,500px)]">
      {/* HERO panel */}
      <aside className="relative hidden flex-col overflow-hidden lg:flex">
        {/* Cover photo */}
        <img
          src="/assets/litmus-cover-photo.png"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0 bg-litmus-dark-green-900/72"
          aria-hidden
        />

        {/* Spacer — so content sits at ~55% height, not bottom */}
        <div className="h-[30vh]" />

        {/* Center+bottom: app name and tagline grouped */}
        <div className="relative px-10 pb-12">
          <div className="relative pb-2">
            <img
              src="/assets/Litmus_Logo_Reverse.png"
              alt="Litmus"
              className="h-12 w-auto object-contain"
            />
          </div>
          <h1 className="font-display text-[52px] font-bold leading-[1.05] tracking-tight text-white">
            Catalog Agent
          </h1>

          <div className="mt-8 border-t border-white/10 pt-8">
            <h2 className="font-display text-[30px] leading-[1.12] tracking-tight text-white">
              Catalog every{" "}
              <span className="text-litmus-light-green-400">tag</span>,
              <br />
              from <span className="italic">every</span> system,
              <br />
              on <span className="text-litmus-light-green-400">every</span>{" "}
              plant floor.
            </h2>
            <p className="mt-4 max-w-md text-[13.5px] leading-relaxed text-white/55">
              On-prem connector for the Litmus Catalog Agent. Crawls local PLCs,
              SCADA, historians, and databases — ships the metadata up the wire.
            </p>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">
              v1 · {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </aside>

      {/* FORM panel */}
      <div className="grid place-items-center bg-paper px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-6 lg:hidden">
            <img
              src="/assets/Litmus_Logo.png"
              alt="Litmus"
              className="h-8 w-auto object-contain"
            />
          </div>

          <div className="eyebrow">Sign in</div>
          <h1 className="mt-2 font-display text-[28px] font-semibold tracking-tight text-ink">
            Welcome back.
          </h1>
          <p className="mt-2 text-[13.5px] text-ash">
            Use the credentials your administrator gave you at enrollment.
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Username
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                required
                className="input"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Password
              </span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ash hover:text-ink"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-[18px] w-[18px]"
                      aria-hidden="true"
                    >
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-[18px] w-[18px]"
                      aria-hidden="true"
                    >
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58a2 2 0 0 0 2.83 2.83" />
                      <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                      <path d="M6.61 6.61C4.13 8.36 2 12 2 12a13.16 13.16 0 0 0 1.67 2.68A10.94 10.94 0 0 0 12 19" />
                    </svg>
                  )}
                </button>
              </div>
            </label>
          </div>

          {error ? (
            <div className="mt-4 rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              {error}
            </div>
          ) : null}

          <p className="mt-4 text-center text-[12px] leading-relaxed text-ash">
            {'By clicking "Sign In", you agree to the Litmus Automation Inc. '}
            <button
              type="button"
              onClick={() => setIsEulaOpen(true)}
              className="font-medium text-litmus-dark-green-700 underline hover:text-litmus-dark-green-600"
            >
              End User License Agreement
            </button>
            .
          </p>

          <button
            type="submit"
            disabled={busy}
            className="btn-primary mt-4 w-full justify-center py-2.5"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>

          <div className="mt-8 border-t border-rule pt-4 text-center">
            <a
              href="/agent.crt"
              download
              className="text-[12px] font-medium text-ash hover:text-ink"
            >
              ↓ Trust this agent's certificate
            </a>
          </div>
        </form>

        <EulaModal
          open={isEulaOpen}
          mode="readonly"
          onClose={() => setIsEulaOpen(false)}
        />
      </div>
    </div>
  );
}
